import { Link } from "react-router-dom";
import { colors, fonts, spacing } from "@edufadder/ui";
import { useExtensionStatus } from "../lib/extension-status.js";
import * as s from "../styles.js";

/**
 * A practice article for trying the extension. It is the ONE page on this site
 * that asks EduFadder to annotate it (see components/Layout.tsx).
 */
export function PracticePage(): JSX.Element {
  const ext = useExtensionStatus();
  return (
    <div style={{ ...s.wide, maxWidth: 820 }}>
      <div role="note" style={{ ...s.card, background: colors.goldTint, borderLeft: `6px solid ${colors.gold}` }}>
        <strong>Practice page.</strong> This sample article was written by the EduFadder team to show the extension at work. It
        deliberately mentions several popular education ideas — some well supported by research, some not.{" "}
        {ext.installed ? (
          ext.autoDetect === "on" ? (
            <>With the extension installed, the ideas are highlighted and a summary opens in the corner by itself — select one for the full research note.</>
          ) : (
            <>Automatic detection is off: click the EduFadder toolbar icon and choose <em>Check this page anyway</em>.</>
          )
        ) : (
          <>
            <Link to="/get-extension">Install the extension</Link> to see it in action, or paste a paragraph into{" "}
            <Link to="/check">Check a passage</Link>.
          </>
        )}
      </div>

      <article style={{ marginTop: spacing(8) }}>
        <p style={{ ...s.mutedText, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Parenting &amp; school · sample article</p>
        <h1 style={{ fontFamily: fonts.serif, fontSize: 36, lineHeight: 1.2, margin: `${spacing(2)} 0 ${spacing(6)}` }}>
          Five ways to help your child learn this school year
        </h1>
        <div style={{ ...s.prose, fontSize: 18 }}>
          <p>
            A new school year is a fresh start. Here are five tips we've gathered from teachers, parents, and a few popular
            books to help your child make the most of it.
          </p>
          <h2 style={{ fontFamily: fonts.serif }}>1. Find out how your child learns best</h2>
          <p>
            Every child is different. Some students are visual learners, while others are auditory or kinesthetic learners,
            so teachers should identify each student's learning style and teach to it.
          </p>
          <h2 style={{ fontFamily: fonts.serif }}>2. Unlock their hidden potential</h2>
          <p>Did you know we only use 10 percent of our brains? The right activities can help children unlock the rest.</p>
          <h2 style={{ fontFamily: fonts.serif }}>3. Put on some Mozart</h2>
          <p>Playing classical music to babies boosts their intelligence, which is why many parents play Mozart at home.</p>
          <h2 style={{ fontFamily: fonts.serif }}>4. Study smart before tests</h2>
          <p>The best way to study is to reread your notes and highlight the key points the night before the exam.</p>
          <p>
            Then again, research shows that testing yourself on the material helps you remember it longer than just reading it
            again — so try a few flashcards, too.
          </p>
          <h2 style={{ fontFamily: fonts.serif }}>5. Little and often</h2>
          <p>Studying a little each day works better than cramming the night before the test.</p>
        </div>
      </article>
      <p style={{ ...s.mutedText, marginTop: spacing(8) }}>
        Curious what EduFadder found? Every idea above has an entry in the <Link to="/edufads">edufads library</Link>.
      </p>
    </div>
  );
}
